import { CONSTANTS } from "@/constants";
import { Button, PdfIcon } from "@netfront/ui-library";

interface LessonAsset {
  title: string;
  presignedUrl: string;
  tagList: { name: string }[];
}

interface LessonAssetsProps {
  linkedAssets: LessonAsset[];
  roleTag?: string;
}

const { TAGS } = CONSTANTS;

const getAssetLabel = (title: string) => {
  const lower = title.toLowerCase();
  if (lower.includes('summar')) return 'Summary';
  if (lower.includes('activit')) return 'Activities';
  return title;
};

const WhitePdfIcon = ({ className }: { className?: string }) => (
  <PdfIcon className={`${className ?? ''} color-white`} />
);

const sortSummaryFirst = (a: LessonAsset, b: LessonAsset) =>
  (a.title.toLowerCase().includes('summar') ? 0 : 1) - (b.title.toLowerCase().includes('summar') ? 0 : 1);

const AssetGroup = ({ heading, assets }: { heading: string; assets: LessonAsset[] }) => (
  <div className="">
    <div className="flex items-center gap-2 mb-4">
      <div className="bg-black rounded-circle p-2 w-8 h-8 flex items-center justify-center">
        <WhitePdfIcon  />
      </div>
      <p className="mb-0 weight-600 ">{heading}</p>
    </div>
    <ul className="list-style-none" aria-label="Lesson resources">
      {assets.map(({ title, presignedUrl }, index) => (
        <li key={`asset-${presignedUrl}-${index}`} className="ml-0">
          <Button
            key={presignedUrl}
            linkButton={{ id: title, url: presignedUrl, target: '_blank' }}
            text={getAssetLabel(title)}
            variant="primary-inverse"
            size="small"
          />
        </li>
      ))}
    </ul>
  </div>
);

export const LessonAssets = ({ linkedAssets, roleTag }: LessonAssetsProps) => {
  const isNotLogbookPdf = ({ tagList }: LessonAsset) => tagList.every(({ name }) => name !== TAGS.LOGBOOK_PDF.NAME);

  if (roleTag === 'Teacher') {
    const teacherAssets = linkedAssets
      .filter(isNotLogbookPdf)
      .filter(({ tagList }) => tagList.some(({ name }) => name === TAGS.TEACHER.NAME))
      .sort(sortSummaryFirst);

    const studentAssets = linkedAssets
      .filter(isNotLogbookPdf)
      .filter(({ tagList }) => tagList.some(({ name }) => name === TAGS.STUDENT.NAME))
      .sort(sortSummaryFirst);

    if (teacherAssets.length === 0 && studentAssets.length === 0) return null;

    return (
      <div className="flex gap-8">
        {teacherAssets.length > 0 && <AssetGroup heading="For Teachers" assets={teacherAssets} />}
        {studentAssets.length > 0 && <AssetGroup heading="For Students" assets={studentAssets} />}
      </div>
    );
  }

  const assets = linkedAssets
    .filter(isNotLogbookPdf)
    .filter(({ tagList }) => tagList.some(({ name }) => name === roleTag))
    .sort(sortSummaryFirst);

  if (assets.length === 0) return null;

  return (
    <ul className="flex items-end gap-2 list-style-none" aria-label="Lesson resources">
      {assets.map(({ title, presignedUrl }, index) => (
        <li key={`student-resource-${index}`} className="ml-0">
          <Button
            key={presignedUrl}
            icon={PdfIcon}
            iconPosition="after"
            linkButton={{ id: title, url: presignedUrl, target: '_blank' }}
            text={getAssetLabel(title)}
            variant="primary-inverse"
            size="small"
          />
        </li>
      ))}
    </ul>
  );
};
